import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material'
import { Link } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SearchIcon from '@mui/icons-material/Search'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import ExportMenu from '../../Shared/ExportMenu'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import {
  createProductService,
  deleteProductService,
  editProductService,
  getUniqueProductService,
  searchProductService,
} from '../../../services/productService'
import { mensagemDeErro } from '../../../services/apiClient'
import { formatBRL, formatDateTime } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'

/**
 * Os nomes são os do contrato da API, não os do sistema antigo: `reference` e
 * `info` chamavam-se `referencia` e `informationAdditional`. Errar a grafia não
 * dá erro nenhum — o zod da rota descarta campo que não declara, então os dois
 * sumiam calados no salvar e voltavam vazios na listagem.
 */
const emptyForm = {
  name: '',
  description: '',
  idProductPartner: '',
  reference: '',
  sku: '',
  typeProduct: '',
  brand: '',
  supplier: '',
  price: '',
  barCode: '',
  color: '',
  size: '',
  info: '',
  active: true,
}

const Product = () => {
  const { notify, FeedbackSnackbar } = useFeedback()
  const podeGerenciar = usePode(PERMISSOES.PRODUTO_GERENCIAR)

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [situation, setSituation] = useState('all')

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  useDebounce(
    () => {
      setSearchTerm(search)
      setPage(0)
    },
    500,
    [search]
  )

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await searchProductService({
        SearchWord: searchTerm,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })
      setRows(response.data.result || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar os produtos.'), 'error')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [searchTerm, rowsPerPage, page, notify])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const visibleRows = useMemo(() => {
    if (situation === 'active') return rows.filter(row => row.active)
    if (situation === 'inactive') return rows.filter(row => !row.active)
    return rows
  }, [rows, situation])

  // Export leva todo o filtro, não só a página: pagina a API em lotes e aplica
  // o mesmo recorte de situação da tela.
  const fetchAllForExport = useCallback(async () => {
    const BLOCO = 200
    const todas = []
    let offset = 0
    for (;;) {
      const response = await searchProductService({ SearchWord: searchTerm, limit: BLOCO, offset })
      const itens = response.data.result || []
      todas.push(...itens)
      offset += BLOCO
      if (offset >= (response.data.total || 0) || itens.length === 0) break
    }
    if (situation === 'active') return todas.filter(row => row.active)
    if (situation === 'inactive') return todas.filter(row => !row.active)
    return todas
  }, [searchTerm, situation])

  const updateForm = (field, value) => setForm(current => ({ ...current, [field]: value }))

  const handleCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const handleEdit = async productId => {
    setSaving(true)
    try {
      const response = await getUniqueProductService(productId)
      const product = response.data
      setForm({
        name: product.name || '',
        description: product.description || '',
        idProductPartner: product.idProductPartner || '',
        reference: product.reference || '',
        sku: product.sku || '',
        typeProduct: product.typeProduct || '',
        brand: product.brand || '',
        supplier: product.supplier || '',
        price: product.price ?? '',
        barCode: product.barCode || '',
        color: product.color || '',
        size: product.size || '',
        info: product.info || '',
        active: product.active ?? true,
      })
      setEditingId(productId)
      setDialogOpen(true)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar o produto selecionado.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify('Informe o nome do produto.', 'warning')
      return
    }
    setSaving(true)
    try {
      if (editingId) {
        await editProductService({ ...form, productId: editingId })
        notify('Produto atualizado com sucesso!')
      } else {
        await createProductService(form)
        notify('Produto criado com sucesso!')
      }
      setDialogOpen(false)
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar o produto.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setSaving(true)
    try {
      await deleteProductService(deleteTarget)
      notify('Produto excluído com sucesso!')
      setDeleteTarget(null)
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível excluir o produto.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const dataColumns = [
    { id: 'name', label: 'Nome', element: row => row.name || '—' },
    { id: 'description', label: 'Descrição', element: row => row.description || '—' },
    {
      id: 'idProductPartner',
      label: 'Identificação ERP',
      element: row => row.idProductPartner || '—',
    },
    { id: 'reference', label: 'Referência', element: row => row.reference || '—' },
    { id: 'sku', label: 'SKU', element: row => row.sku || '—' },
    { id: 'typeProduct', label: 'Tipo Produto', element: row => row.typeProduct || '—' },
    { id: 'brand', label: 'Marca', element: row => row.brand || '—' },
    { id: 'supplier', label: 'Fabricante', element: row => row.supplier || '—' },
    { id: 'price', label: 'Preço', align: 'right', element: row => formatBRL(row.price) },
    { id: 'barCode', label: 'Cód. Barras', element: row => row.barCode || '—' },
    { id: 'color', label: 'Cor', element: row => row.color || '—' },
    { id: 'size', label: 'Tamanho', element: row => row.size || '—' },
    {
      id: 'active',
      label: 'Ativo',
      element: row => (
        <Chip
          size="small"
          label={row.active ? 'Sim' : 'Não'}
          color={row.active ? 'success' : 'default'}
        />
      ),
    },
    { id: 'created', label: 'Criação', element: row => formatDateTime(row.createdAt) },
  ]

  const columns = [
    {
      id: 'actions',
      label: 'Ações',
      width: 110,
      element: row => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={podeGerenciar ? 'Editar' : 'Sem permissão para editar produtos'}>
            <span>
              <IconButton size="small" disabled={!podeGerenciar} onClick={() => handleEdit(row.id)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={podeGerenciar ? 'Excluir' : 'Sem permissão para excluir produtos'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!podeGerenciar}
                onClick={() => setDeleteTarget(row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
    ...dataColumns,
  ]

  const exportColumns = [
    { header: 'Nome', accessor: 'name' },
    { header: 'Descrição', accessor: 'description' },
    { header: 'Identificação ERP', accessor: 'idProductPartner' },
    { header: 'Referência', accessor: 'reference' },
    { header: 'SKU', accessor: 'sku' },
    { header: 'Tipo Produto', accessor: 'typeProduct' },
    { header: 'Marca', accessor: 'brand' },
    { header: 'Fabricante', accessor: 'supplier' },
    { header: 'Preço', accessor: row => formatBRL(row.price) },
    { header: 'Cód. Barras', accessor: 'barCode' },
    { header: 'Cor', accessor: 'color' },
    { header: 'Tamanho', accessor: 'size' },
    { header: 'Ativo', accessor: row => (row.active ? 'Sim' : 'Não') },
    { header: 'Criação', accessor: row => formatDateTime(row.createdAt) },
  ]

  return (
    <Box>
      <PageHeader
        title="Produtos"
        subtitle="Cadastro de produtos da empresa"
        actions={
          <>
            <ExportMenu
              rows={visibleRows}
              resolveRows={fetchAllForExport}
              columns={exportColumns}
              fileName="produtos"
              title="Produtos"
              disabled={total === 0}
            />
            <PermissionComponent permissao={PERMISSOES.PRODUTO_GERENCIAR}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                component={Link}
                to="/chargeProduct"
              >
                Carga em lote
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                Criar
              </Button>
            </PermissionComponent>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Pesquisar"
              value={search}
              onChange={event => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="product-situation-label">Situação</InputLabel>
              <Select
                labelId="product-situation-label"
                label="Situação"
                value={situation}
                onChange={event => setSituation(event.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Ativos</MenuItem>
                <MenuItem value="inactive">Inativos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        columns={columns}
        rows={visibleRows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        rowKey={row => row.id}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? 'Editar produto' : 'Novo produto'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Nome"
                value={form.name}
                onChange={event => updateForm('name', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Descrição"
                value={form.description}
                onChange={event => updateForm('description', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Identificação ERP"
                value={form.idProductPartner}
                onChange={event => updateForm('idProductPartner', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Referência"
                value={form.reference}
                onChange={event => updateForm('reference', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="SKU"
                value={form.sku}
                onChange={event => updateForm('sku', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tipo Produto"
                value={form.typeProduct}
                onChange={event => updateForm('typeProduct', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Marca"
                value={form.brand}
                onChange={event => updateForm('brand', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fabricante"
                value={form.supplier}
                onChange={event => updateForm('supplier', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Preço"
                value={form.price}
                onChange={event => updateForm('price', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cód. Barras"
                value={form.barCode}
                onChange={event => updateForm('barCode', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Cor"
                value={form.color}
                onChange={event => updateForm('color', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Tamanho"
                value={form.size}
                onChange={event => updateForm('size', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Informação Adicional"
                value={form.info}
                onChange={event => updateForm('info', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.active}
                    onChange={event => updateForm('active', event.target.checked)}
                  />
                }
                label="Ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Excluir produto"
        description="Tem certeza que deseja excluir esse produto?"
        confirmLabel="Excluir"
        loading={saving}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <LoadingBackdrop open={saving} message="Processando..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default Product
